import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toggleCaseTranscriptDrawer } from "./CaseTranscriptDrawer";
import {
  MODIFIED_CASE_DOCUMENTATION,
  MODIFIED_CASE_DOCUMENTATION_ENTITY,
} from "../types/CommonTypes";
import formatDateTime from "../../../helpers/formatDateTime";
import Divider from "../../../components/Divider";

interface CaseDocumentationItemProps {
  detail: MODIFIED_CASE_DOCUMENTATION;
}
type EntityValue = string | number | boolean | null;

const getEntryValue = (
  entityType?: string,
  entityValue?: EntityValue,
): EntityValue => {
  const type = (entityType ?? "").toLowerCase();

  if (type === "boolean" || type === "checkbox" || type === "enum") {
    return typeof entityValue === "string"
      ? entityValue.trim()
      : entityValue ?? null;
  }

  if (type === "text" || type === "number" || type === "time") {
    return entityValue ?? null;
  }

  return !!entityValue;
};

const DEFAULT_ENUM_OPTIONS = ["Yes", "No", "N/A"];

const isYesNoNaEnum = (enumValues?: Array<string>): boolean => {
  if (!enumValues || enumValues.length < 2 || enumValues.length > 3) {
    return false;
  }

  const normalized = enumValues.map(value => {
    const option = value.trim().toLowerCase();
    return option === "na" ? "n/a" : option;
  });

  return (
    new Set(normalized).size === normalized.length &&
    normalized.every(option => ["yes", "no", "n/a"].includes(option))
  );
};

const ENTITY_TYPE_PRIORITY: Record<string, number> = {
  enum: 0,
  checkbox: 1,
  time: 2,
  number: 3,
  text: 4,
};

const getEntityTypePriority = (entityType: string): number =>
  ENTITY_TYPE_PRIORITY[entityType] ?? 4;

const getEnumOptionStyle = (label: string): { bg: string; color: string } => {
  const normalized = label.trim().toLowerCase();

  if (normalized === "yes") return { bg: "#E6F8ED", color: "#1E8E3E" };
  if (normalized === "no") return { bg: "#FEE2E2", color: "#DC2626" };
  if (normalized === "n/a" || normalized === "na")
    return { bg: "#F1F1F3", color: "#6B7280" };

  return { bg: "#F2F0FF", color: "#7A5AF8" };
};

const EntryTriStateBadge: FC<{ options: Array<string>; value: EntityValue }> = ({
  options,
  value,
}) => {
  const items = options?.length ? options : DEFAULT_ENUM_OPTIONS;
  const selectedValue =
    typeof value === "string" ? value.trim().toLowerCase() : null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        border: "1px solid #E0E0E0",
        borderRadius: "999px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {items.map((label, idx) => {
        const isSelected = selectedValue === label.trim().toLowerCase();
        const nextIsSelected =
          selectedValue === items[idx + 1]?.trim().toLowerCase();
        const showDivider =
          idx < items.length - 1 && !isSelected && !nextIsSelected;
        const style = getEnumOptionStyle(label);

        return (
          <Box
            key={label}
            sx={{
              px: 1.75,
              py: 0.5,
              fontSize: 13,
              lineHeight: "20px",
              whiteSpace: "nowrap",
              fontWeight: isSelected ? 600 : 500,
              color: isSelected ? style.color : "#6B7280",
              backgroundColor: isSelected ? style.bg : "transparent",
              borderRight: showDivider ? "1px solid #E0E0E0" : "none",
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
};
interface EntryCellProps {
  title: string;
  entityType: string;
  entityValue: EntityValue;
  entry: EntityValue;
  enumValues: Array<string>;
}

const EntryCell: FC<EntryCellProps> = ({
  title,
  entityType,
  entityValue,
  entry,
  enumValues,
}) => {
  const { t } = useTranslation();

  const isEnumBadgeType = entityType === "checkbox" || entityType === "enum";
  const isEmptyValue =
    entityValue == null ||
    (typeof entityValue === "string" && entityValue.trim() === "");

  if (isEnumBadgeType) {
    if (isYesNoNaEnum(enumValues)) {
      return (
        <EntryTriStateBadge
          options={enumValues}
          value={isEmptyValue ? null : entry}
        />
      );
    }

    return (
      <Typography>{isEmptyValue ? t("Not_asked") : String(entry)}</Typography>
    );
  }

  if (isEmptyValue) {
    return <Typography>{t("Not_asked")}</Typography>;
  }

  if (typeof entry === "boolean") {
    return <Checkbox checked={entry} />;
  }

  return <Typography>{entry}</Typography>;
};

interface TimestampCellProps {
  timestamp: { title: string; subTitle: string | null | undefined } | null;
}

const TimestampCell: FC<TimestampCellProps> = ({ timestamp }) => {
  if (!timestamp) {
    return <Typography color="text.secondary">--</Typography>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Typography sx={{ fontSize: 14 }}>{timestamp.title}</Typography>
      <Typography sx={{ fontSize: 12, color: "gray" }}>
        {timestamp.subTitle}
      </Typography>
    </Box>
  );
};

const getConfidenceText = (confidence: string | null): string => {
  if (!confidence) return "--";
  return confidence.includes("%") ? confidence : `${confidence}%`;
};

const buildDocumentRow = (
  d: MODIFIED_CASE_DOCUMENTATION_ENTITY,
  t: (key: string) => string
) => {
  const { createdAt, updatedBy, confidence, evidence, transcript, entityValue } =
    d?.latestEntry ?? {};

  const entry = getEntryValue(d.entityType, entityValue);
  const entityType = (d.entityType ?? "").toLowerCase();

  return {
    title: d.entityName,
    entry,
    entityType,
    entityValue: entityValue ?? null,
    enumValues: d.enumValues,
    badge: "",
    timestamp: entityValue
      ? {
          title: formatDateTime(createdAt, true, "hh:mm a"),
          subTitle: updatedBy,
        }
      : null,
    confidence: confidence ? `${confidence * 100}` : null,
    actions: {
      title: t("View_Transcript"),
      disabled: !entry,
      color: entry ? "#5921F2" : "#AC90F9",
      cursor: entry ? "pointer" : "default",
      onPress: entry
        ? () => toggleCaseTranscriptDrawer(evidence ?? transcript ?? "")
        : undefined,
    },
  };
};

type DocumentRow = ReturnType<typeof buildDocumentRow>;

const compareDocumentRows = (a: DocumentRow, b: DocumentRow): number => {
  const aHasValue = a.entityValue != null ? 0 : 1;
  const bHasValue = b.entityValue != null ? 0 : 1;
  if (aHasValue !== bHasValue) return aHasValue - bHasValue;

  const typeDiff =
    getEntityTypePriority(a.entityType) - getEntityTypePriority(b.entityType);
  if (typeDiff !== 0) return typeDiff;

  const aConfidence = a.confidence != null ? Number(a.confidence) : -Infinity;
  const bConfidence = b.confidence != null ? Number(b.confidence) : -Infinity;
  return bConfidence - aConfidence;
};

interface DocumentationRowProps {
  docData: DocumentRow;
  isLast: boolean;
  rowIndex: number;
}

const DocumentationRow: FC<DocumentationRowProps> = ({
  docData,
  isLast,
  rowIndex,
}) => {
  const { t } = useTranslation();
  const confidenceText = getConfidenceText(docData.confidence);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
        borderBottom: isLast ? "none" : "1px solid #EEEEEE",
        borderBottomLeftRadius: isLast ? 2 : 0,
        borderBottomRightRadius: isLast ? 2 : 0,
        py: 1,
        px: 1.5,
      }}
    >
      <Box flex={1.5}>
        <Typography sx={{ fontWeight: 500 }}>{docData.title}</Typography>
      </Box>

      <Box flex={0.7}></Box>

      <Box flex={1} textAlign="center">
        <EntryCell
          title={docData.title}
          entityType={docData.entityType}
          entityValue={docData.entityValue}
          entry={docData.entry}
          enumValues={docData.enumValues}
        />
      </Box>

      <Box
        flex={1}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TimestampCell timestamp={docData.timestamp} />
      </Box>

      <Box flex={1} textAlign="center">
        <Typography
          sx={{
            color: "#000",
            fontWeight: 500,
          }}
        >
          {confidenceText}
        </Typography>
      </Box>

      <Box
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        <Typography
          component="span"
          onClick={docData.actions.onPress}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            textDecoration: "underline",
            color: docData.actions.color,
            cursor: docData.actions.cursor,
            userSelect: "none",
          }}
        >
          {t("View_Transcript")}
        </Typography>
      </Box>
    </Box>
  );
};

const EmptyDocumentationState: FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      p: 2,
      borderBottomLeftRadius: 2,
      borderBottomRightRadius: 2,
      textAlign: "center",
      color: "text.secondary",
    }}
  >
    {label}
  </Box>
);

const CaseDocumentationItem: FC<CaseDocumentationItemProps> = ({ detail }) => {
  const { sectionName, entity, actualEntries } = detail;

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const documents = entity.map((d) => buildDocumentRow(d, t));
  const normalizeName = (name?: string): string =>
    (name ?? "").replace(/\s+/g, "").toLowerCase();
  const isSectionRow = (row: DocumentRow): boolean => {
    const title = normalizeName(row.title);
    return !!title && normalizeName(sectionName).includes(title);
  };
  documents.sort((a, b) => {
    const aIsSection = isSectionRow(a) ? 0 : 1;
    const bIsSection = isSectionRow(b) ? 0 : 1;
    if (aIsSection !== bIsSection) return aIsSection - bIsSection;

    return compareDocumentRows(a, b);
  });

  const isEntryNotEmpty = documents.length - 1 > 0;
  const badgeLabel =
    actualEntries > 0 ? actualEntries?.toString() : t("Not_logged");

  const isNotEmpty = documents.length > 0;

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.2s",
      }}
    >
      <Box
        onClick={toggleExpand}
        display="flex"
        alignItems="center"
        px={1.5}
        py={1}
      >
        <Box flex={1.5}>
          <Typography sx={{ fontWeight: 600 }}>{sectionName}</Typography>
        </Box>

        <Box flex={0.7}>
          <Chip
            label={badgeLabel}
            sx={{
              backgroundColor: "#f2f0ff",
              color: "#7a5af8",
              fontWeight: isEntryNotEmpty ? 600 : 400,
              fontSize: 13,
              borderRadius: "12px",
            }}
          />
        </Box>

        <Box flex={1} textAlign="center">
          <Typography sx={{ color: "#555", fontWeight: 500 }}>
            {t("Entry")}
          </Typography>
        </Box>
        <Box flex={1} textAlign="center">
          <Typography sx={{ color: "#555", fontWeight: 500 }}>
            {t("Timestamp")}
          </Typography>
        </Box>
        <Box flex={1} textAlign="center">
          <Typography sx={{ color: "#555", fontWeight: 500 }}>
            {t("Confidence")}
          </Typography>
        </Box>
        <Box flex={1} textAlign="center">
          <Typography sx={{ color: "#555", fontWeight: 500 }}>
            {t("Evidence")}
          </Typography>
        </Box>

        <IconButton onClick={toggleExpand} size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        {isNotEmpty ? (
          documents.map((docData, i) => (
            <DocumentationRow
              key={docData.title}
              docData={docData}
              isLast={documents.length === i + 1}
              rowIndex={i}
            />
          ))
        ) : (
          <EmptyDocumentationState label={t("No_Documentation_Data")} />
        )}
      </Collapse>
    </Paper>
  );
};

export default CaseDocumentationItem;
